import type { DailyNoteCategoryRow, DailyWeekNoteDraft } from '../types/dailyRecordNote';
import { dailyCategorySystemKey, isGeneralDailyCategory } from './dailyNoteCategoryEdit';

export function isDailyWeekDraftId(id: string): boolean {
  return id.startsWith('__draft-');
}

export function createDailyWeekDraftNote(
  noteDate: string,
  body: string,
  categoryId: string | null,
  id?: string
): DailyWeekNoteDraft {
  return {
    id: id && id.trim() ? id : `__draft-${crypto.randomUUID()}`,
    note_date: noteDate,
    body,
    category_id: categoryId,
    persisted: false,
  };
}

export function defaultDailyCategoryId(categories: DailyNoteCategoryRow[]): string | null {
  return categories.find((c) => isGeneralDailyCategory(c))?.id ?? null;
}

export function firstCustomDailyCategory(categories: DailyNoteCategoryRow[]): DailyNoteCategoryRow | null {
  const sorted = [...categories].sort(
    (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name) || a.id.localeCompare(b.id)
  );
  return (
    sorted.find((c) => dailyCategorySystemKey(c) === 'sticky') ??
    sorted.find((c) => !isGeneralDailyCategory(c)) ??
    null
  );
}

/** 작성 중인 빈 칸은 그대로 두고, 입력이 끝난 뒤에만 다음 빈 쪽지를 붙인다. */
export function shouldAppendWeeklyStickyComposer(
  stickies: DailyWeekNoteDraft[],
  emptySlotId: string
): boolean {
  const last = stickies[stickies.length - 1];
  if (!last) return true;
  if (last.id === emptySlotId) return false;
  return last.body.trim().length > 0;
}

export function isGeneralDailyWeekNote(
  note: DailyWeekNoteDraft,
  defaultCategoryId: string | null
): boolean {
  if (!note.category_id) return true;
  if (defaultCategoryId && note.category_id === defaultCategoryId) return true;
  return false;
}

export function generalNoteForDate(
  notes: DailyWeekNoteDraft[],
  dateIso: string,
  defaultCategoryId: string | null
): DailyWeekNoteDraft | null {
  const generals = notes.filter(
    (n) => n.note_date === dateIso && isGeneralDailyWeekNote(n, defaultCategoryId)
  );
  if (generals.length === 0) return null;
  if (generals.length === 1) return generals[0];
  return {
    ...generals[0],
    body: generals.map((n) => n.body).join('\n\n'),
  };
}

export function collapseGeneralNotes(
  notes: DailyWeekNoteDraft[],
  defaultCategoryId: string | null
): { notes: DailyWeekNoteDraft[]; extraIds: string[] } {
  const extraIds: string[] = [];
  const out: DailyWeekNoteDraft[] = [];
  const generalIndexByDate = new Map<string, number>();
  for (const n of notes) {
    if (!isGeneralDailyWeekNote(n, defaultCategoryId)) {
      out.push(n);
      continue;
    }
    const idx = generalIndexByDate.get(n.note_date);
    if (idx == null) {
      generalIndexByDate.set(n.note_date, out.length);
      out.push(n);
    } else {
      extraIds.push(n.id);
      const cur = out[idx];
      out[idx] = {
        ...cur,
        body: [cur.body, n.body].filter((b) => b.trim().length > 0).join('\n\n'),
      };
    }
  }
  return { notes: out, extraIds };
}

export function stickyNotesForDate(
  notes: DailyWeekNoteDraft[],
  dateIso: string,
  defaultCategoryId: string | null
): DailyWeekNoteDraft[] {
  return notes.filter(
    (n) => n.note_date === dateIso && !isGeneralDailyWeekNote(n, defaultCategoryId)
  );
}

export function customDailyCategories(categories: DailyNoteCategoryRow[]): DailyNoteCategoryRow[] {
  return [...categories]
    .filter((c) => !isGeneralDailyCategory(c))
    .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
}

export function setGeneralBodyForDate(
  notes: DailyWeekNoteDraft[],
  dateIso: string,
  body: string,
  defaultCategoryId: string | null
): DailyWeekNoteDraft[] {
  const generals = notes.filter(
    (n) => n.note_date === dateIso && isGeneralDailyWeekNote(n, defaultCategoryId)
  );
  const others = notes.filter(
    (n) => !(n.note_date === dateIso && isGeneralDailyWeekNote(n, defaultCategoryId))
  );
  if (generals.length === 0) {
    if (!body) return notes;
    return [...others, createDailyWeekDraftNote(dateIso, body, defaultCategoryId)];
  }
  const [first] = generals;
  if (!first.persisted && !body) return others;
  return [...others, { ...first, body }];
}

export function upsertStickyNote(
  notes: DailyWeekNoteDraft[],
  note: DailyWeekNoteDraft
): DailyWeekNoteDraft[] {
  const idx = notes.findIndex((n) => n.id === note.id);
  if (idx < 0) return [...notes, note];
  const next = notes.slice();
  next[idx] = note;
  return next;
}

export function patchStickyNote(
  notes: DailyWeekNoteDraft[],
  id: string,
  patch: Partial<Pick<DailyWeekNoteDraft, 'body' | 'category_id'>>
): DailyWeekNoteDraft[] {
  return notes.flatMap((n) => {
    if (n.id !== id) return [n];
    const next = { ...n, ...patch };
    if (!next.persisted && !next.body.trim()) return [];
    return [next];
  });
}

export function prepareWeekNotesForSave(
  notes: DailyWeekNoteDraft[],
  defaultCategoryId: string | null
): DailyWeekNoteDraft[] {
  const { notes: collapsed } = collapseGeneralNotes(notes, defaultCategoryId);
  return collapsed.filter((n) => {
    if (isGeneralDailyWeekNote(n, defaultCategoryId)) {
      return n.persisted || n.body.trim().length > 0;
    }
    return n.body.trim().length > 0;
  });
}

export function dateHasSavedWeekNoteContent(
  notes: DailyWeekNoteDraft[],
  dateIso: string
): boolean {
  return notes.some((n) => n.note_date === dateIso && n.persisted && n.body.trim().length > 0);
}

export function weekNotesSignature(notes: DailyWeekNoteDraft[]): string {
  return notes
    .filter((n) => n.persisted || n.body.trim().length > 0)
    .map((n) => `${n.id}\t${n.note_date}\t${n.category_id ?? ''}\t${n.body}`)
    .sort()
    .join('\n');
}

export function mergeWeekNotesKeepingLocalDirty(
  incoming: DailyWeekNoteDraft[],
  local: DailyWeekNoteDraft[],
  baseline: DailyWeekNoteDraft[]
): DailyWeekNoteDraft[] {
  const baselineById = new Map(baseline.map((n) => [n.id, n]));
  const incomingById = new Map(incoming.map((n) => [n.id, n]));
  const kept = new Map<string, DailyWeekNoteDraft>();

  for (const n of incoming) {
    const loc = local.find((x) => x.id === n.id);
    const base = baselineById.get(n.id);
    if (loc && base && weekNotesSignature([loc]) !== weekNotesSignature([base])) {
      kept.set(loc.id, loc);
    } else {
      kept.set(n.id, n);
    }
  }

  for (const n of local) {
    if (kept.has(n.id)) continue;
    const base = baselineById.get(n.id);
    if (!n.persisted || (base && weekNotesSignature([n]) !== weekNotesSignature([base]))) {
      kept.set(n.id, n);
    } else if (!incomingById.has(n.id) && n.persisted) {
      /* 서버에서 사라진 persisted 는 버림 */
    }
  }

  return Array.from(kept.values());
}
